import { useQuery } from '@tanstack/react-query';

import { getHttpClient, useHttpClient } from '../lib/http-client';
import { getQueryClient } from '../lib/query-client';
import { DimensionKey } from '../types/recognition';
import { headers } from './meta';

// Re-export for backwards compatibility
export { DimensionKey } from '../types/recognition';

enum QueryKey {
	QualityScores = 'recognition-analysis:quality-scores',
	QualitySuggestions = 'recognition-analysis:suggestions',
	DraftAssist = 'recognition-analysis:draft-assist',
}

export const qualityScoresQueryKey = (text?: string) => [QueryKey.QualityScores, text].filter(Boolean);
export const qualitySuggestionsQueryKey = (params: { dimensionKey: string; text: string }) => [QueryKey.QualitySuggestions, params];

export const qualityScoresPath = (prefix = '') => `${prefix}/recognition/quality_scores`;
export const qualitySuggestionsPath = (prefix = '') => `${prefix}/recognition/quality_suggestions`;
export const qualityDraftAssistPath = (prefix = '') => `${prefix}/bizy/recognition/quality_draft_assist`;

export const draftAssistQueryKey = (params: { draftText: string; dimensionKey: string; recipientIds: string[]; attachedValues: string[] }) =>
	[QueryKey.DraftAssist, params] as const;

export type DraftAssistAPIData = {
	suggestion: string;
	targetDimension: DimensionKey;
	confidence: number;
	conversationId: string;
};

export type ConversationOutcomeSignal = 'submitted' | 'discarded' | 'abandoned';

export const signalConversationOutcome = async (conversationId: string, outcome: ConversationOutcomeSignal) => {
	const http = await getHttpClient();
	await http.post(`/bizy/conversations/${conversationId}/outcome`, { outcome }, { headers });
};

export const loadDraftAssist = async (params: {
	draftText: string;
	dimensionKey: string;
	recipientIds: string[];
	conversationId: string | null;
	source: string;
	pagePath: string;
	trigger: string;
	missingDimensions: string[];
	attachedValues: string[];
}) => {
	const http = await getHttpClient();
	return (
		await http.post<DraftAssistAPIData>(
			qualityDraftAssistPath(),
			{
				draftText: params.draftText,
				dimensionKey: params.dimensionKey,
				recipientIds: params.recipientIds,
				conversationId: params.conversationId,
				source: params.source,
				pagePath: params.pagePath,
				trigger: params.trigger,
				missingDimensions: params.missingDimensions,
				attachedValues: params.attachedValues,
			},
			{ headers }
		)
	).data;
};

export type QualityScoreAPIData = {
	version: number;
	dimensions: Array<{
		key: DimensionKey;
		reason: string;
		matches: boolean;
		examples?: string[];
		degraded?: boolean;
	}>;
};

export const loadQualityScore = async (text: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: qualityScoresQueryKey(text),
		queryFn: async () => {
			return (await http.post<QualityScoreAPIData>(qualityScoresPath(), { bonusText: text }, { headers })).data;
		},
		retry: false,
	});
};

export const getQualityScores = async () => {
	const queryClient = await getQueryClient();
	return queryClient.getQueriesData<QualityScoreAPIData>(qualityScoresQueryKey());
};

type QualitySuggestionsAPIData = {
	version: number;
	key: DimensionKey;
	matches: boolean;
	suggestions: string[];
};

export const useQualitySuggestions = ({ dimensionKey, text }: { dimensionKey: DimensionKey; text: string }) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!text,
		queryKey: qualitySuggestionsQueryKey({ dimensionKey, text }),
		queryFn: async () => {
			return (await http.post<QualitySuggestionsAPIData>(qualitySuggestionsPath(), { bonusText: text, dimensionKey }, { headers })).data;
		},
		retry: false,
	});
};
